// The boot shell: the pieces of this package put together the way most games want them. It reads the platform and
// picks the tier from the game's table, refuses to start without WebGL2 (the fail screen says why), runs the game's
// boot steps behind the loading screen, turns any step failure or timeout into the fail screen, and keeps the boot
// report an agent reads (`__game.boot()`): steps and durations, module bytes, the progress trace, the first frame.

import { moduleBytes, type ModuleBytes } from "./bytes";
import { detectPlatform, pickTier, type DeviceKind, type PlatformInfo } from "./platform";
import { createBootScreen, type BootScreen, type BootText } from "./screens";
import { BootStepError, runBoot, type BootProgress, type BootRun, type BootStep, type StepRecord } from "./steps";

/** The shell's options: everything game-specific comes in here. */
export interface BootShellOptions {
  /** Player-facing strings for the loading and fail screens. */
  text: BootText;
  /** The boot steps, in dependency order. */
  steps: readonly BootStep[];
  /** The game's tier per device class, e.g. `{ phone: "low", desktop: "high" }`. */
  tiers: Readonly<Record<DeviceKind, string>>;
  /** A tier the page asked for (a URL value); wins when known. */
  tierOverride?: string | null;
  /** Modules to report: name → URL pattern of its files. A step with the same id gives the module's init time. */
  modules?: Readonly<Record<string, RegExp>>;
  /** Default step timeout, ms (30 000). */
  timeoutMs?: number;
  /** Where the screens go (document.body). */
  parent?: HTMLElement;
  /** Class-name prefix of the screens ("rpg-boot"). */
  prefix?: string;
  /** Planted defects of the screens, set before the run. */
  faults?: Partial<BootScreen["faults"]>;
  /** The platform, when the game has read it already (tests). Default: detectPlatform(). */
  platform?: PlatformInfo;
}

/** One module's line in the report. */
export interface ModuleReport extends ModuleBytes {
  /** Its step finished. */
  ready: boolean;
  /** Its step's duration (download, compile and init), ms; null without a step of that id. */
  initMs: number | null;
}

/** The boot report. */
export interface BootReport {
  /** "running" → "ok", or "failed" with the step and error. */
  status: "running" | "ok" | "failed";
  failedStep: string | null;
  error: string | null;
  platform: PlatformInfo;
  tier: string;
  tierSource: "override" | "device";
  steps: StepRecord[];
  progress: BootProgress[];
  /** What the bar showed, with times. */
  shown: { value: number; t: number }[];
  modules: Record<string, ModuleReport>;
  /** Whether the module gzip sizes have been measured yet. */
  measured: boolean;
  /** From the shell's start to the last step's end, ms. */
  totalMs: number | null;
  /** Since navigation: when boot finished, and when the game drew its first frame (`markFirstFrame`), ms. */
  readyAtMs: number | null;
  firstFrameAtMs: number | null;
}

/** A running boot shell. */
export interface BootShell {
  readonly platform: PlatformInfo;
  readonly tier: string;
  readonly screen: BootScreen;
  /** Run the steps (once). Resolves with their results; rejects (after showing the fail screen) on a failure. */
  run(): Promise<Record<string, unknown>>;
  /** Show the fail screen for an exception outside the steps (the game's own boot code). */
  fail(error: unknown): void;
  /** Note the first frame drawn (call from the render loop; later calls are ignored). */
  markFirstFrame(): void;
  /** Gzip-measure the modules' files (after boot; fills `report().modules[*].gzipBytes`). */
  measure(): Promise<void>;
  /** The report so far (a fresh object each call). */
  report(): BootReport;
}

/** Build the shell; the loading screen shows at once. Call `run()` to boot. */
export function createBootShell(o: BootShellOptions): BootShell {
  const now = (): number => performance.now();
  const platform = o.platform ?? detectPlatform();
  const { tier, source } = pickTier(platform.kind, o.tiers, { override: o.tierOverride });
  const screen = createBootScreen({ text: o.text, parent: o.parent, prefix: o.prefix, now });
  Object.assign(screen.faults, o.faults);
  const t0 = now();
  let run: BootRun | null = null;
  let running: Promise<Record<string, unknown>> | null = null;
  let status: BootReport["status"] = "running";
  let failedStep: string | null = null;
  let error: string | null = null;
  let totalMs: number | null = null;
  let readyAt: number | null = null;
  let firstFrameAt: number | null = null;
  let bytes: Record<string, ModuleBytes> = {};
  let measured = false;

  const fail = (e: unknown): void => {
    if (status === "failed") return;
    status = "failed";
    failedStep = e instanceof BootStepError ? e.step : failedStep;
    error = e instanceof Error ? e.message : String(e);
    screen.fail(platform.webgl2 ? "exception" : "webgl2", e);
    console.error(e);
  };

  return {
    platform,
    tier,
    screen,
    run() {
      running ??= (async () => {
        if (!platform.webgl2) {
          const e = new Error("WebGL2 is not available");
          fail(e);
          throw e;
        }
        run = runBoot(o.steps, { timeoutMs: o.timeoutMs, now, onProgress: (p) => screen.progress(p), onStep: (r) => screen.step(r) });
        try {
          const r = await run.done;
          totalMs = r.ms;
          readyAt = now();
          status = "ok";
          screen.complete();
          return r.results;
        } catch (e) {
          fail(e);
          throw e;
        }
      })();
      return running;
    },
    fail,
    markFirstFrame() {
      firstFrameAt ??= now();
    },
    async measure() {
      if (!o.modules) return;
      bytes = await moduleBytes(o.modules, { gzip: true });
      measured = true;
    },
    report() {
      const steps = (run as BootRun | null)?.steps ?? o.steps.map((s) => ({ id: s.id, caption: s.caption, units: s.units ?? 1, done: 0, status: "pending" as const, startMs: 0, ms: 0 }));
      const modules: Record<string, ModuleReport> = {};
      for (const name of Object.keys(o.modules ?? {})) {
        const rec = steps.find((s) => s.id === name);
        const b = bytes[name] ?? { files: [], bytes: 0, transferBytes: 0, gzipBytes: null };
        modules[name] = { ...b, ready: rec ? rec.status === "ok" || rec.status === "warn" : false, initMs: rec && rec.status !== "pending" ? Math.round(rec.ms * 10) / 10 : null };
      }
      return {
        status,
        failedStep,
        error,
        platform,
        tier,
        tierSource: source,
        steps: steps.map((s) => ({ ...s })),
        progress: [...((run as BootRun | null)?.progress ?? [])],
        shown: [...screen.shown],
        modules,
        measured,
        totalMs: totalMs ?? (status === "running" ? null : now() - t0),
        readyAtMs: readyAt,
        firstFrameAtMs: firstFrameAt,
      };
    },
  };
}
